// Role helpers — mirrors the backend multi-role model.
// A user can hold any combination of:
//   "administrator" — full access (settings, integrations, user management).
//   "approver"      — approves/rejects submitted expenses + gets review pings.
//   "submitter"     — captures and submits expenses (implicit baseline).
//   "accountant"    — STANDALONE read-only role. Sees everything globally
//                     (Dashboard / Transactions / Expenses / Approvals) but
//                     cannot submit, edit, approve, reject, push, or change
//                     settings. Never combined with the other roles.
// Legacy single `user.role` is kept on the API payload for backwards-compat;
// always prefer `user.roles[]` when present.
export const ROLE_LABELS = {
  administrator: "Administrator",
  approver: "Approver",
  submitter: "Submitter",
  accountant: "Accountant",
  // Legacy fallbacks
  admin: "Administrator",
  employee: "Submitter",
};

export const ROLE_OPTIONS = [
  { value: "administrator", label: "Administrator", description: "Manages settings, integrations & users" },
  { value: "approver", label: "Approver", description: "Approves / rejects submitted expenses" },
  { value: "submitter", label: "Submitter", description: "Captures & submits expenses (always on)" },
  { value: "accountant", label: "Accountant", description: "Read-only: views all expenses, transactions & approvals (no editing)" },
];

function getRoles(user) {
  if (!user) return [];
  if (Array.isArray(user.roles) && user.roles.length) return user.roles;
  // Derive from legacy single-role field
  const legacy = user.role || "submitter";
  if (legacy === "admin" || legacy === "administrator") {
    return ["administrator", "approver", "submitter"];
  }
  if (legacy === "approver") return ["approver", "submitter"];
  if (legacy === "accountant") return ["accountant"];
  return ["submitter"];
}

export function userRoles(user) {
  return getRoles(user);
}

export function hasRole(user, role) {
  return getRoles(user).includes(role);
}

export function roleLabel(role) {
  return ROLE_LABELS[role] || (role ? role.charAt(0).toUpperCase() + role.slice(1) : "");
}

/** Pretty multi-role label, e.g. "Administrator + Approver". */
export function userRolesLabel(user) {
  const roles = getRoles(user).filter((r) => r !== "submitter");
  if (roles.length === 0) return ROLE_LABELS.submitter;
  return roles.map(roleLabel).join(" + ");
}

export function isAdmin(user) {
  return hasRole(user, "administrator");
}

export function canApprove(user) {
  // Approval power lives ONLY on the approver role — admins must also hold it.
  return hasRole(user, "approver");
}

export function isAccountant(user) {
  // Read-only standalone role. UI components use this to hide every mutation
  // affordance (New Expense, Edit, Submit, Approve, Reject, Re-push, Delete,
  // Settings, etc.) and to grant view access to the Approvals page.
  return hasRole(user, "accountant");
}

export function canViewAllData(user) {
  // Mirror of backend `_can_view_all` — true when the user should see the
  // company-wide list of expenses/transactions/approvals (admin + accountant)
  // instead of being scoped to their own submissions.
  return isAdmin(user) || isAccountant(user);
}
